import React from "react";
import { Receipt, Star, Wallet } from "lucide-react";
import { LoadPhase, SkeletonCard } from "../../../components/ui";
import { formatRupees } from "../../users";
import type { DashboardSummary } from "../types/dashboard.types";

/** The card's own footprint, matched to `StatTile` so the top row is one band
 *  of equal-height cards and the block that stands in for it is the size of the
 *  card that lands in its place. */
const FOOTPRINT = "min-h-[120px] sm:min-h-[150px] 3xl:min-h-[180px]";

/**
 * One of the three figures set along the foot of the greeting.
 *
 * These are here rather than in tiles of their own because they are not the
 * same kind of fact as the counts beside them: the tiles answer "how many
 * accounts are there", which is the state of the business, and these three are
 * this month's takings and this month's arrivals — a note on how it is going,
 * which belongs with the greeting that opens the screen.
 */
interface InlineStatProps {
  icon: React.ReactNode;
  /** The pale disc the icon sits in, as Tailwind background/text classes. */
  tint: string;
  value: string;
  label: string;
}

const InlineStat: React.FC<InlineStatProps> = ({ icon, tint, value, label }) => (
  <div className="flex items-center gap-3 min-w-0">
    <span
      aria-hidden="true"
      className={`w-10 h-10 3xl:w-12 3xl:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${tint}`}
    >
      {icon}
    </span>
    <div className="min-w-0">
      <p className="text-[15px] 3xl:text-[17px] font-semibold text-ink truncate">
        {value}
      </p>
      <p className="text-[12px] 3xl:text-[14px] text-ink-muted truncate">
        {label}
      </p>
    </div>
  </div>
);

/**
 * What a figure reads as when the summary never arrived.
 *
 * The em dash the tables already print for a missing value, and the same rule
 * the tiles beside this card follow: a load that failed shows nothing rather
 * than a zero. "₹0 monthly revenue" under a failed request is not a gap, it is
 * a claim — and the wrong one.
 */
const NO_FIGURE = "—";

interface WelcomeCardProps {
  /** Who is signed in, as they are greeted — a first name, or "Admin" when the
   *  profile has not landed. The greeting reads fine either way. */
  name: string;
  /** The figures along the foot. Absent until the summary arrives. */
  summary?: DashboardSummary;
  phase?: LoadPhase;
  /** Layout only — where this sits in the page's grid. */
  className?: string;
}

/** The card the screen opens on: who is reading, and the three figures that say
 *  how the month is going. */
const WelcomeCard: React.FC<WelcomeCardProps> = ({
  name,
  summary,
  phase = "ready",
  className = "",
}) =>
  phase !== "ready" ? (
    <SkeletonCard phase={phase} className={`${FOOTPRINT} ${className}`} />
  ) : (
    <section
      className={`rounded-md border border-line bg-white p-4 sm:p-5 3xl:p-7 flex flex-col justify-between gap-6 ${FOOTPRINT} ${className}`}
    >
      <div>
        {/* Sized as a heading but not marked up as one: the page's own <h1> is
            in the header above, and a second heading here would claim a
            section of the outline that does not exist. */}
        <p className="text-[20px] sm:text-[22px] 3xl:text-[26px] font-semibold text-ink">
          Hello, {name}
        </p>
        <p className="mt-1 text-[13px] 3xl:text-[15px] text-ink-muted">
          Welcome to your Naurist workspace
        </p>
      </div>

      {/* Three equal columns rather than three figures pushed up against the
          left edge: set in a row they read as one band across the foot of the
          card, and the eye can run down the same three positions every time
          rather than hunting for where the second one starts. Stacked below
          `sm`, where a third of a narrow card would clip every one of them. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 3xl:gap-x-6 gap-y-4">
        <InlineStat
          icon={<Star className="w-[18px] h-[18px]" />}
          tint="bg-[#DDF3E4] text-[#2F8E5B]"
          value={summary ? `${summary.newThisMonth} accounts` : NO_FIGURE}
          label="New this month"
        />
        <InlineStat
          icon={<Wallet className="w-[18px] h-[18px]" />}
          tint="bg-[#EDE7F6] text-[#5B3FA8]"
          value={summary ? formatRupees(summary.annualRevenue) : NO_FIGURE}
          label="Annual revenue"
        />
        <InlineStat
          icon={<Receipt className="w-[18px] h-[18px]" />}
          tint="bg-[#DEEBFB] text-[#2F6BAE]"
          value={summary ? formatRupees(summary.monthlyRevenue) : NO_FIGURE}
          label="Monthly revenue"
        />
      </div>
    </section>
  );

export default WelcomeCard;
